import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FilterListIcon from '@mui/icons-material/FilterList';
import RefreshIcon from '@mui/icons-material/Refresh';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

import ModalBoxes from '../../../../common/feedback/ModalBoxes/ModalBoxes';
import { OP_KINDS } from '../../../../constants/aiAnalytics';
import { chartOpKey } from '../../../../services/aiAnalytics/operationResume.service';
import { AI_COLORS, SAVED_CHART_HEIGHT } from '../aiAnalyticsConstants';
import { parseRefreshedData } from '../services/aiDataUtils';
import { detectChartType, isTableSpec, TABLE_DISPLAY_TYPE } from '../services/chartTypeUtils';
import { exportRowsToExcel } from '../services/excelExport';

import ChartFilters from './ChartFilters';
import ChartTypeToggle from './ChartTypeToggle';
import InlineProgress from './InlineProgress';
import ResultMetaBar from './ResultMetaBar';
import { applyFilters, buildAvailableFields, normalizeRows } from './SavedChartView.service';
import SqlResultTable from './SqlResultTable';
import VegaLiteChart from './VegaLiteChart';

export default function SavedChartView({ chart, onClose, onDelete, onRegenerateType, onSetTableType, onRefreshData }) {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState([]);
  const [switchingToTable, setSwitchingToTable] = useState(false);

  const pendingOp = useSelector(state => state.aiAnalytics.pendingOps[chartOpKey(chart.id)]);
  const refreshing = pendingOp?.kind === OP_KINDS.REFRESH_DATA;
  const busyType = pendingOp?.kind === OP_KINDS.GENERATE_CHART || switchingToTable;

  const { columns, data, executionTimeMs, executedAt, truncated } = useMemo(() => parseRefreshedData(chart), [chart]);
  const normalizedRows = useMemo(() => normalizeRows(data, columns), [data, columns]);
  const availableFields = useMemo(() => buildAvailableFields(columns, normalizedRows), [columns, normalizedRows]);
  const filteredRows = useMemo(() => applyFilters(normalizedRows, filters), [normalizedRows, filters]);
  const filtersActive = filters.length > 0;
  const showAsTable = isTableSpec(chart.vegaSpec);

  async function handleChangeType(type) {
    if (busyType) {
      return;
    }
    if (type === TABLE_DISPLAY_TYPE) {
      if (!onSetTableType) {
        return;
      }
      setSwitchingToTable(true);
      try {
        await onSetTableType(chart.id);
      } finally {
        setSwitchingToTable(false);
      }
    } else if (onRegenerateType) {
      await onRegenerateType(chart.id, type);
    }
  }

  async function handleRefresh() {
    if (!onRefreshData || refreshing) {
      return;
    }
    await onRefreshData(chart.id);
  }

  function handleExportExcel() {
    exportRowsToExcel(columns, filteredRows, chart.title);
  }

  function confirmDelete() {
    ModalBoxes.confirm({
      content: 'Are you sure you want to delete this chart?',
      confirmButton: 'Yes',
      cancelButton: 'No'
    }).then(
      () => {
        onDelete(chart.id);
      },
      () => {}
    );
  }

  return (
    <Box className="saved-chart-view">
      <Box className="saved-chart-header">
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {chart.title}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button color="error" onClick={confirmDelete} sx={{ textTransform: 'none' }}>
            Delete Chart
          </Button>
          <Button
            variant="contained"
            onClick={onClose}
            sx={{ backgroundColor: AI_COLORS.brand, '&:hover': { backgroundColor: AI_COLORS.brandDark } }}
          >
            Close Chart
          </Button>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, mt: 1 }}>
        <ChartTypeToggle value={detectChartType(chart.vegaSpec)} onChange={handleChangeType} busy={busyType} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            size="small"
            startIcon={<FileDownloadIcon />}
            onClick={handleExportExcel}
            disabled={filteredRows.length === 0}
            sx={{ textTransform: 'none', color: AI_COLORS.brand }}
          >
            Export to Excel
          </Button>
          <Button
            size="small"
            startIcon={<FilterListIcon />}
            onClick={() => setFiltersOpen(true)}
            disabled={availableFields.length === 0}
            sx={{ textTransform: 'none', color: AI_COLORS.brand }}
          >
            Filters{filters.length ? ` (${filters.length})` : ''}
          </Button>
          {chart.sqlQuery && (
            <Button
              size="small"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              disabled={refreshing}
              sx={{ textTransform: 'none', color: AI_COLORS.brand }}
            >
              Refresh data
            </Button>
          )}
        </Box>
      </Box>

      {pendingOp && <InlineProgress label={pendingOp.label} />}

      {!showAsTable && (
        <Box sx={{ mb: 2, mt: 1 }}>
          <VegaLiteChart
            spec={chart.vegaSpec}
            data={filteredRows}
            height={SAVED_CHART_HEIGHT}
            overrideSpecData={filtersActive}
          />
          <ResultMetaBar
            executionTimeMs={executionTimeMs}
            executedAt={executedAt}
            truncated={truncated}
            rowCount={data.length}
          />
        </Box>
      )}

      {chart.sqlQuery && (
        <Box sx={{ mb: 2, mt: 2 }}>
          <Typography variant="overline" color="text.secondary">
            Query
          </Typography>
          <Box
            component="pre"
            sx={{
              backgroundColor: AI_COLORS.codeSurfaceLight,
              color: AI_COLORS.codeSurface,
              p: 1.5,
              borderRadius: 1,
              overflow: 'auto',
              fontSize: '0.8rem',
              m: 0,
              whiteSpace: 'pre-wrap'
            }}
          >
            {chart.sqlQuery}
          </Box>
        </Box>
      )}

      <Typography variant="overline" color="text.secondary">
        Data ({filteredRows.length} rows)
      </Typography>
      {filteredRows.length === 0 ? (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {data.length === 0 ? 'The dataset is empty.' : 'No rows match the current filters.'}
          </Typography>
          {showAsTable && (
            <ResultMetaBar
              executionTimeMs={executionTimeMs}
              executedAt={executedAt}
              truncated={truncated}
              rowCount={data.length}
            />
          )}
        </>
      ) : (
        <SqlResultTable
          columns={columns}
          rows={filteredRows}
          footerLeft={
            showAsTable ? (
              <ResultMetaBar
                executionTimeMs={executionTimeMs}
                executedAt={executedAt}
                truncated={truncated}
                rowCount={data.length}
              />
            ) : null
          }
        />
      )}

      <ChartFilters
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        availableFields={availableFields}
        filters={filters}
        onChange={setFilters}
      />
    </Box>
  );
}
